import { supabase } from "../lib/supabase";

/**
 * Compresses and uploads signature PNG to Supabase Storage
 * @param base64Data - Base64 encoded PNG data (without data URL prefix)
 * @param contractId - Optional contract ID for organizing files
 * @returns Storage path for the uploaded signature (e.g., "user-id/contract-123.png")
 */
export async function uploadSignatureToStorage(
  base64Data: string,
  contractId?: string
): Promise<string> {
  try {
    // Get authenticated user
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      throw new Error("User not authenticated");
    }

    console.log("Starting signature upload for user:", user.id);

    // Convert base64 to Blob
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: "image/png" });
    console.log("Original blob size:", blob.size, "bytes");

    // Compress PNG using canvas
    const compressedBlob = await compressPNG(blob);
    console.log(
      "Compressed blob size:",
      compressedBlob.size,
      "bytes",
      `(${Math.round((1 - compressedBlob.size / blob.size) * 100)}% reduction)`
    );

    // Generate filename: userId/contractId-timestamp-random.png or userId/timestamp-random.png
    const timestamp = Date.now();
    const randomSuffix = Math.random().toString(36).substring(2, 8);
    const filename = contractId
      ? `${user.id}/${contractId}-${timestamp}-${randomSuffix}.png`
      : `${user.id}/${timestamp}-${randomSuffix}.png`;

    console.log("Uploading to:", filename);

    // Upload to storage
    const { data, error } = await supabase.storage
      .from("contract-signatures")
      .upload(filename, compressedBlob, {
        contentType: "image/png",
        cacheControl: "3600",
        upsert: false,
      });

    if (error) {
      console.error("Storage upload error:", error);
      throw new Error(`Failed to upload signature: ${error.message}`);
    }

    console.log("Upload successful, path:", data.path);
    return data.path;
  } catch (error) {
    console.error("Error in uploadSignatureToStorage:", error);
    throw error;
  }
}

/**
 * Compresses PNG image using canvas
 * Optimizes signature images (typically black on white) very well
 * @param blob - Original PNG blob
 * @returns Compressed PNG blob
 */
async function compressPNG(blob: Blob): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);

    img.onload = () => {
      try {
        // Create canvas with same dimensions
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          console.warn("Canvas context not available, returning original blob");
          URL.revokeObjectURL(url);
          resolve(blob);
          return;
        }

        // Draw image (keep transparency, no white background)
        ctx.drawImage(img, 0, 0);

        // Convert to blob with compression
        canvas.toBlob(
          (compressedBlob) => {
            URL.revokeObjectURL(url);
            if (compressedBlob) {
              // Use compressed version only if it's actually smaller
              resolve(compressedBlob.size < blob.size ? compressedBlob : blob);
            } else {
              console.warn("Compression failed, returning original blob");
              resolve(blob);
            }
          },
          "image/png",
          0.8 // Compression quality (0.8 is a good balance)
        );
      } catch (error) {
        console.error("Error during compression:", error);
        URL.revokeObjectURL(url);
        resolve(blob); // Fallback to original on error
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image for compression"));
    };

    img.src = url;
  });
}

/**
 * Gets the public URL for a signature path
 * @param signaturePath - Storage path to the signature file
 * @returns Public URL for displaying the signature
 */
export function getSignatureUrl(signaturePath: string): string {
  const { data } = supabase.storage
    .from("contract-signatures")
    .getPublicUrl(signaturePath);

  return data.publicUrl;
}

/**
 * Downloads signature from storage and converts to base64 data URL
 * Used for PDF generation and display
 * @param signaturePath - Storage path or legacy data URL
 * @returns Base64 data URL (e.g., "data:image/png;base64,iVBORw0...")
 */
export async function downloadSignatureAsBase64(
  signaturePath: string
): Promise<string> {
  try {
    // If it's already a data URL (legacy format), return it
    if (signaturePath.startsWith("data:")) {
      return signaturePath;
    }

    console.log("Downloading signature from storage:", signaturePath);

    // Get the public URL from the path
    const publicUrl = getSignatureUrl(signaturePath);

    // Download the file
    const response = await fetch(publicUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch signature: ${response.statusText}`);
    }

    const blob = await response.blob();

    // Convert blob to base64 data URL
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error("Failed to read signature blob"));
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error downloading signature from storage:", error);
    throw error;
  }
}

/**
 * Deletes a signature from storage (for cleanup/rollback)
 * @param signaturePath - Storage path to delete
 */
export async function deleteSignatureFromStorage(
  signaturePath: string
): Promise<void> {
  try {
    console.log("Deleting signature:", signaturePath);

    const { error } = await supabase.storage
      .from("contract-signatures")
      .remove([signaturePath]);

    if (error) {
      throw error;
    }

    console.log("Signature deleted successfully");
  } catch (error) {
    console.error("Error deleting signature:", error);
    throw error;
  }
}
